import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { MainLayout as Layout } from './features/layout/components/MainLayout';
import { ErrorBoundary, Spinner } from './components/common';

import { ToastProvider } from './contexts/ToastContext';
import { JobProgressProvider } from './contexts/JobProgressContext';
import { PWAProvider } from './contexts/PWAProvider';
import { UpdateToast } from './components/pwa/UpdateToast';
import { InstallBanner } from './components/pwa/InstallBanner';

const PublicSignInPage = React.lazy(() => import('./features/attendance/components/PublicSignInPage').then(m => ({ default: m.PublicSignInPage })));
const InviteMismatchPage = React.lazy(() => import('./features/auth/components/InviteMismatchPage').then(m => ({ default: m.InviteMismatchPage })));
const InviteStatePage = React.lazy(() => import('./features/auth/components/InviteStatePage').then(m => ({ default: m.InviteStatePage })));
const InviteGatewayPage = React.lazy(() => import('./features/invitations/components/InviteGatewayPage').then(m => ({ default: m.InviteGatewayPage })));
const PendingApprovalPage = React.lazy(() => import('./features/auth/components/PendingApprovalPage').then(m => ({ default: m.PendingApprovalPage })));
const SetupWizard = React.lazy(() => import('./features/auth/components/SetupWizard').then(m => ({ default: m.SetupWizard })));
const AdminLayout = React.lazy(() => import('./features/admin/components/AdminLayout').then(m => ({ default: m.AdminLayout })));

// Dev-only imports (tree-shaken in production)
const DevLayout = import.meta.env.DEV
    ? React.lazy(() => import('./modules/dev/DevLayout').then(m => ({ default: m.DevLayout })))
    : null;
const KitchenSinkPage = import.meta.env.DEV
    ? React.lazy(() => import('./modules/dev/KitchenSink').then(m => ({ default: m.KitchenSink })))
    : null;

const routeFallback = (
    <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
    </div>
);

function lazyRoute(element: React.ReactNode): React.ReactNode {
    return (
        <React.Suspense fallback={routeFallback}>
            {element}
        </React.Suspense>
    );
}

/**
 * Main Application Component
 */
export const App: React.FC = () => {
    return (
        <React.StrictMode>
            <BrowserRouter>
                <ToastProvider>
                    <JobProgressProvider>
                        <PWAProvider>
                            <InstallBanner />
                            <Routes>
                                {/* Public sign-in route (no auth required) */}
                                <Route path="/sign-in/:orgId/:projectId/:token" element={lazyRoute(<PublicSignInPage />)} />
                                
                                {/* Pending approval page */}
                                <Route path="/pending-approval" element={lazyRoute(<PendingApprovalPage />)} />
                                <Route path="/invite-mismatch" element={lazyRoute(<InviteMismatchPage />)} />
                                <Route path="/invite-state/:status" element={lazyRoute(<InviteStatePage />)} />
                                <Route path="/invite/:orgId/:projectId/:invitationId/:token" element={lazyRoute(<InviteGatewayPage />)} />
                                
                                {/* Setup wizard (authenticated) */}
                                <Route path="/setup" element={
                                    <ErrorBoundary tabName="Setup">
                                        {lazyRoute(<SetupWizard />)}
                                    </ErrorBoundary>
                                } />
                                
                                {/* Admin portal (authenticated) */}
                                <Route path="/admin/*" element={
                                    <ErrorBoundary tabName="Admin Portal">
                                        {lazyRoute(<AdminLayout />)}
                                    </ErrorBoundary>
                                } />

                                {/* Dev sandbox routes (dev only, no auth, no MainLayout) */}
                                {import.meta.env.DEV && DevLayout && KitchenSinkPage ? <Route path="/dev" element={
                                        <React.Suspense fallback={<div className="p-8 text-center">Loading dev tools…</div>}>
                                            <DevLayout />
                                        </React.Suspense>
                                    }>
                                        <Route path="kitchen-sink" element={
                                            <React.Suspense fallback={<div className="p-8 text-center">Loading…</div>}>
                                                <KitchenSinkPage />
                                            </React.Suspense>
                                        } />
                                    </Route> : null}

                                {/* Main app (authenticated) */}
                                <Route path="/*" element={<Layout />} />
                            </Routes>
                            <UpdateToast />
                        </PWAProvider>
                    </JobProgressProvider>
                </ToastProvider>
            </BrowserRouter>
        </React.StrictMode>
    );
};

export default App;













